import React, { FC, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, Typography, DialogActions } from "@mui/material";

import { getVisualCommentMessageId } from "../../../pages/dashboard/tabs/journal/comment-types";

import { IResponseVisualTrainComments, IComment } from "../../../pages/dashboard/tabs/journal/interfaces";

import { MediaRenderer } from "../../common/media-renderer";
import { StyledButton } from "../../common/styled/button";
import { useVisualCommentFiles } from "./use-visual-comment-files";

const getCommentDisplayText = (comment: IComment, formatMessage: ReturnType<typeof useIntl>["formatMessage"]) => {
  const messageId = getVisualCommentMessageId(comment.commentType);
  return messageId ? formatMessage({ id: messageId }) : comment.comment;
};

interface ICommentDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  data: IResponseVisualTrainComments | null;
  loading: boolean;
}

export const CommentDetailsDialog: FC<ICommentDetailsDialogProps> = ({ open, onClose, data, loading }) => {
  const { formatMessage } = useIntl();

  const { commentFiles, downloadCommentFile } = useVisualCommentFiles({ comments: data?.comments, enabled: open });
  const [isImageDialogOpen, setIsImageDialogOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
    setIsImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setIsImageDialogOpen(false);
    setTimeout(() => {
      setSelectedImage(null);
    }, 300);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: "700" }}>
          <FormattedMessage id="dialogs.comment" />
        </DialogTitle>
        <DialogContent sx={{ minHeight: "60px" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : (
            data && (
              <>
                {(data.user?.position != null || data.user?.surname || data.user?.name || data.user?.patronym) && (
                  <Typography>
                    {`${
                      data.user?.position != null
                        ? formatMessage({ id: `enums.userGroup.${data.user.position}` }) + ": "
                        : ""
                    }${data.user?.surname || ""} ${data.user?.name ? data.user.name.charAt(0) + "." : ""}${
                      data.user?.patronym ? data.user.patronym.charAt(0) + "." : ""
                    }`}
                  </Typography>
                )}
                {data.paramAnswer && <Typography>{data.paramAnswer}</Typography>}
                {data.comments.map((comment, index) => {
                  const file = commentFiles[comment.commentId];

                  return (
                    <React.Fragment key={comment.commentId}>
                      <Typography sx={{ marginTop: 2 }}>{getCommentDisplayText(comment, formatMessage)}</Typography>
                      {file && (
                        <Box
                          mt={2}
                          display="flex"
                          flexWrap="wrap"
                          justifyContent="flex-start"
                          alignItems="flex-start"
                          width="100%"
                          height="100%"
                        >
                          <MediaRenderer
                            file={file}
                            index={index}
                            onImageClick={handleImageClick}
                            onDownload={() => void downloadCommentFile(file)}
                          />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )
          )}
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={onClose} sx={{ minWidth: 80, mb: 1, mr: 2 }}>
            <FormattedMessage id="form.buttons.ok" />
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isImageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "auto",
            maxWidth: "lg",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "700" }}>
          <FormattedMessage id="dialogs.photo" />
        </DialogTitle>
        <DialogContent dividers={true} sx={{ overflow: "auto" }}>
          {selectedImage && (
            <Box display="flex" justifyContent="flex-start" alignItems="flex-start" width="100%" height="100%">
              <img src={selectedImage} alt="Full size" style={{ width: "auto", height: "auto" }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseImageDialog} sx={{ minWidth: 80, mb: 1, mr: 2 }}>
            <FormattedMessage id="form.buttons.ok" />
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
